import { SEO } from '@/components/common/SEO'
import { FatFooterLayout } from '@/layouts/FatFooterLayout'
import { NextPage } from 'next'
import home_page_img from '@/public/images/site-preview.gif'
import { Button } from '@/components/controls/Button'
import { P } from '@/components/common/P'
import { MainHero } from '@/components/heros/MainHero'
import { H2 } from '@/components/headlines/H2'
import { H1 } from '@/components/headlines/H1'
import { Crown } from '@/components/icons/Crown'
import Image from 'next/image'
import erin_photo from '@/public/images/erin_photo.webp'
import shane_photo from '@/public/images/shane_photo.webp'
import { imageLoader } from '@/loaders/index'
import { RoundedContainer } from '@/components/containers/RoundedContainer'
import { Container } from '@/components/common/Container'
import calendar from '@/public/images/calendar.webp'
import coaches from '@/public/images/coaches_img.png'
import integrations from '@/public/images/integrations.png'
import { Step } from '@/components/decors/Step'
import { TestimonialBig } from '@/components/common/TestimonialBig'
import { SimpleCarouselItem } from '@/components/carousel/SimpleCarousel'
import { SimpleCarousel } from '@/components/carousel/SimpleCarousel'
import { CustomTrustpilot } from '@/components/common/CustomTrustpilot'
import tino_ava from '@/public/images/tino_ava.png'
import tyler_ava from '@/public/images/tyler_ava.png'
import luke_ava from '@/public/images/luke_ava.png'
import skye_ava from '@/public/images/testim_ava1.jpg'
import testim_ava2 from '@/public/images/testim_ava2.jpg'
import testim_ava3 from '@/public/images/XayLi_Barclay.jpeg'
import testim_ava5 from '@/public/images/ross.jpg'
import { useElementWidth } from '@/hooks/ElementWidth'
import { useBetterMediaQuery } from '@/hooks/BetterMediaQuery'
import { Accordion } from '@/components/blocks/Accordion'
import { RecommendedIn } from '@/components/common/RecommendedIn'
import { eliteCoachingFAQs } from 'data/faqs'

const testimonials = [
  {
    name: 'Skye K',
    text: 'In the 3 months I’ve been with Commit Action my revenue has increased by 50% and I’ve hired a team to help me execute which has bought my total hours worked down to 25 per week (while growing 2 companies)',
    avatarUrl: skye_ava.src,
    caption: 'Founder | Profit Partnerships Group'
  },
  {
    name: 'Luke A.',
    text: 'Before working with them I was struggling with prioritising, doing 100 hour weeks every week mainly on tasks that didn’t matter. We worked on leveraging my actions to team members, and I had my 2nd highest sales month ($20,000)...',
    avatarUrl: luke_ava.src,
    caption: 'Founder | Badass Business'
  },
  {
    name: 'Tino T.',
    text: 'Commit Action has been game changing for my business. I went from feeling overwhelmed and scattered with to-dos to having clarity on my yearly goals, milestones to hit, and daily tasks that would get me there. ',
    avatarUrl: tino_ava.src,
    caption: 'Founder | Measured Growth'
  },
  {
    name: 'Tyler Dudding',
    text: 'I have been using Commit Action for almost two months now, I’ve made more progress in my business in 2 months than in the previous 2 years, I’ve enrolled more clients than ever before and hit my highest income month ever...',
    avatarUrl: tyler_ava.src,
    caption: 'Dating Coach & Creator'
  },
  {
    name: 'XayLi Barclay',
    text: 'Since signing up for Commit Action, I’ve been able to blow through my initial goals and create even bigger ones. Meeting with my coach Nick is always one of the highlights of my week!',
    avatarUrl: testim_ava3.src,
    caption: 'Founder & CEO | TDG Visual Solutions'
  },
  {
    name: 'Khierstyn Ross',
    text: 'As an entrepreneur, I constantly feel overwhelmed and second guess if what I’m working on will move me toward my goals, with Commit Action’s focus, now I don’t feel that way as I’m working',
    avatarUrl: testim_ava5.src,
    caption: 'Founder & CEO | Launch & Scale™'
  },
  {
    name: 'Steve Krakower',
    text: 'The results speak for themselves — in the few months that I have been working working with Commit Action, our revenue has increased 25% and we finally hit our first 6-figure month',
    avatarUrl: testim_ava2.src,
    caption: 'Founder & CEO | Harbor Marketing Agency'
  }
]

const EliteCoaching: NextPage = () => {
  const sm = useBetterMediaQuery('(min-width: 768px)')
  const lg = useBetterMediaQuery('(min-width: 1024px)')
  const xl = useBetterMediaQuery('(min-width: 1280px)')
  const xl2 = useBetterMediaQuery('(min-width: 1680px)')
  const carouselContainerWidth = useElementWidth('testimonials-carousel')

  const carouselWidth = () => {
    if (xl2) {
      return carouselContainerWidth / 5 - 32
    }

    if (xl) {
      return carouselContainerWidth / 4 - 32
    }

    if (lg) {
      return carouselContainerWidth / 3 - 32
    }

    if (sm) {
      return carouselContainerWidth / 2 - 32
    }

    return carouselContainerWidth - 32
  }

  return (
    <>
      <SEO
        title="Elite Coaching"
        description="Join a community of like-minded entrepreneurs to support each other in achieving your goals."
        keywords='Elite Coaching'
        canonicalUrl={process.env.NEXT_PUBLIC_SITE + '/elite-coaching/'}
        ogImageUrl={`${process.env.NEXT_PUBLIC_SITE}${home_page_img.src}`}
      />
      <div className='font--accent'>
        <MainHero>
          <div className='flex flex-col xl:flex-row items-center justify-between  pt-[80px] xl:pt-[100px] pb-[20px] xl:pb-[140px]'>
            <div className='text-center lg:text-left max-w-[520px] text-[18px]'>
              <H1 color='white'>Elite Coaching <br/> for Elite Entrepreneurs</H1>
              <P>Ultra-optimize your focus, execution and lifestyle with white-glove coaching. Become the most efficient powerhouse possible at the center of scaling teams, large businesses and even bigger portfolios.</P>
              <div className='mt-[40px]'>
                <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={'/checkout/elite-coaching'} disableArrow={true} chevron={false} maxWidth={320}>Sign Up Now</Button>
              </div>
            </div>
            <div className='relative max-w-[550px] w-full mt-[80px] xl:mt-0'>
              <div className='md:mr-[50px] flex justify-between items-start'>
                <div className='relative inline-block mt-[-40px] scale-[0.7] xl:scale-[0.9]'>
                  <div className='blurred-box absolute top-[-20px] left-[-30px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[17px] font-bold color--green'>1000+</span>
                      <span className='text-[11px] opacity-[0.7]'>SKUs</span>
                    </div>
                  </div>
                  <div className='blurred-box absolute top-[30px] right-[-30px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[17px] font-bold color--green'>10+</span>
                      <span className='text-[11px] opacity-[0.7]'>Brands</span>
                    </div>
                  </div>
                  <div className='blurred-box absolute bottom-[30px] right-[-50px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[11px] opacity-[0.7]'>eCommerce Aggregation</span>
                    </div>
                  </div>
                  <div className='blurred-box absolute bottom-[-45px] left-[-50px] z-[1]'>
                    <div className='flex flex-col'>
                      <div className='flex items-center'>
                        <span className='text-[18px] font-bold mr-[16px]'>Shane</span>
                        <span className='text-[11px] font-semibold flex items-center mt-[4px]'><Crown className='w-[10px] mr-[4px]' width={10} height={8}/><span>Top Entrepreneur</span></span>
                      </div>
                      <span className='text-[11px] opacity-[0.7] font--primary italic'>Co-Founder & CEO | Emergent</span>
                    </div>
                  </div>
                  <div className='w-[220px] rounded-[10px] overflow-hidden flex drop-shadow-2xl'>
                    <Image loader={imageLoader} src={shane_photo} alt="Shange photo" layout="intrinsic" />
                  </div>
                </div>
                <div className='relative inline-block mt-[200px] xl:mt-[100px] ml-[-80px] xl:ml-0 scale-[0.7] xl:scale-[0.9]'>
                  <div className='blurred-box blurred-box--dark absolute top-[20px] left-[-30px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[17px] font-bold color--green'>8+</span>
                      <span className='text-[11px] opacity-[0.7]'>Locations</span>
                    </div>
                  </div>
                  <div className='blurred-box blurred-box--dark absolute top-[-30px] right-[15px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[17px] font-bold color--green'>80+</span>
                      <span className='text-[11px] opacity-[0.7]'>Staff</span>
                    </div>
                  </div>
                  <div className='blurred-box blurred-box--dark absolute bottom-[30px] left-[-80px] z-[1]'>
                    <div className='flex flex-col'>
                      <span className='text-[11px] opacity-[0.7]'>Luxury Cosmetics <br/> & Wellness</span>
                    </div>
                  </div>
                  <div className='blurred-box blurred-box--dark absolute bottom-[-25px] right-[-50px] z-[1]'>
                    <div className='flex flex-col'>
                      <div className='flex items-center'>
                        <span className='text-[18px] font-bold mr-[16px]'>Erin</span>
                        <span className='text-[11px] font-semibold flex items-center mt-[4px]'><Crown className='w-[10px] mr-[4px]' width={10} height={8}/><span>Top Entrepreneur</span></span>
                      </div>
                      <span className='text-[11px] opacity-[0.7] font--primary italic'>Co-Founder | Skin Perfect Medical</span>
                    </div>
                  </div>
                  <div className='w-[220px] rounded-[10px] overflow-hidden flex drop-shadow-2xl'>
                    <Image loader={imageLoader} src={erin_photo} alt="Erin photo" layout="intrinsic" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <RecommendedIn />
        </MainHero>
        <RoundedContainer className='pt-[80px] xl:pt-[140px]'>
          <Container>
            <div className='flex flex-col xl:flex-row items-center pb-[80px] xl:pb-[140px]'>
              <div className='max-w-[500px] mb-[40px] xl:mb-0'>
                <Step number={1} total={3} />
                <H2>Custom Coaching For Busy Entrepreneurs</H2>
                <P className='mb-[16px]'>Want three 20 minute calls this week while you sprint on a big—fast-moving—opportunity? No problem. Or a longer deep dive call to plan next month’s strategy? No problem.</P>
                <P className='mb-[40px]'>As an elite client, not only do you get twice as much time on calls (and more attention outside of calls) we will also customize the weekly ritual to suit your busy schedule.</P>
                <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={'/checkout/elite-coaching'} disableArrow={true} chevron={false} maxWidth={sm ? 320 : 0}>Sign Up Now</Button>
              </div>
              <div className='xl:ml-[120px] max-w-[580px] flex rounded-[26px] overflow-hidden'>
                <Image loader={imageLoader} src={calendar} alt="Calendar" layout="intrinsic" />
              </div>
            </div>
          </Container>
          <section className='bg-white py-[80px] pb-[120px] xl:pt-[140px] xl:pb-[110px]'>
            <Container>
              <div className='flex flex-col xl:flex-row items-center'>
                <div className='max-w-[470px] order-1 xl:order-2'>
                  <Step number={2} total={3} />
                  <H2>Designed For Teams</H2>
                  <P className='mb-[16px]'>Level up and retain key personnel with coaching across your team. When employees work with a coach they become more independent, feel satisfied in their work, and their effectiveness skyrockets.</P>
                  <P className='mb-[40px]'>The coaching relationship also lets you in on key insights about your team that you would otherwise miss. So you can provide training, resources, or other support for your staff, and watch productivity soar across your business.</P>
                  <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={'/checkout/elite-coaching'} disableArrow={true} chevron={false} maxWidth={sm ? 320 : 0}>Sign Up Now</Button>
                </div>
                <div className='max-w-[790px] order-2 xl:order-1 mt-[40px] scale-[1.5] origin-top-left xl:scale-[1.2]'>
                  <Image loader={imageLoader} src={coaches} alt="Coaching Team" layout="intrinsic" />
                </div>
              </div>
            </Container>
          </section>
          <RoundedContainer style='dark' className='py-[80px] pb-[50px] xl:py-[140px] overflow-hidden' doubleRounded numberOfCircles={1}>
            <Container>
              <div className='flex flex-col xl:flex-row items-center pb-[80px] xl:pb-[140px]'>
                <div className='max-w-[500px] mb-[40px] xl:mb-0'>
                  <Step number={3} total={3} />
                  <H2 color='white'>Coaching With An Insider</H2>
                  <P className='mb-[40px] text-white/70'>Get a coach who is always up to speed with what’s going on across your businesses. As an Elite client you can add your coach into Asana, Slack, or any tool you use, so they truly feel like part of your team, turning up to every call knowing exactly what’s on your plate.</P>
                  <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={'/checkout/elite-coaching'} disableArrow={true} chevron={false} maxWidth={sm ? 320 : 0}>Sign Up Now</Button>
                </div>
                <div className='xl:ml-[120px] max-w-[580px] flex flex-col items-center rounded-[26px] overflow-hidden'>
                  <P className='text-white/100 opacity-[1] mb-[10px] xl:mb-[20px] font-bold'>Share with</P>
                  <div className='ml-[-24px] xl:ml-0 mr-[-24px] xl:mr-0'>
                    <Image loader={imageLoader} src={integrations} alt="Integrations" layout="intrinsic" />
                  </div>
                  <P className='text-white/70 mt-[-30px] xl:mt-[-20px]'>or whatever tech you use…</P>
                </div>
              </div>
              <div className="border-white border-[4px] rounded-[20px] p-[24px] py-[40px] xl:p-[40px] relative mb-[60px] xl:mb-[0px]">
                <span className="absolute top-[-53px] xl:top-[-60px] left-6 text-[180px] xl:text-[220px] leading-[1] bg-[#111821] text-white">“</span>
                <P className='text-white/100 opacity-[1] !text-[18px] xl:!text-[32px] relative p-[20px] xl:p-[80px] text-center'>My coach alerted me to some difficulties one of my top sales performers was having. I would have never known, but was able to help out and unblock him and since then his revenue contribution has gone off the charts. Having Elizabeth help me close that gap was worth what I’ve paid the whole year. And my team loves it too.</P>
                <span className="absolute right-6 bottom-[-136px] xl:bottom-[-165px] text-[180px] xl:text-[220px] leading-[1] bg-[#111821] text-white">”</span>
              </div>
            </Container>
          </RoundedContainer>
          <section className='pt-[80px] xl:pt-[140px] pb-[80px] xl:pb-[180px]'>
            <Container>
              <div>
                <div className='flex flex-col xl:items-center xl:text-center'>
                  <H2>The End Result…</H2>
                  <P className='max-w-[800px] mb-[40px]'>Your concierge for evidence-based productivity and management best practices, so you can perform at the absolute cutting edge of entrepreneurial effectiveness.</P>
                  <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={'/checkout/elite-coaching'} disableArrow={true} chevron={false} maxWidth={sm ? 320 : 0}>Sign Up Now</Button>
                </div>
                <div className='flex scale-[0.9] mb-[20px] xl:mb-[55px] justify-center mt-[60px] xl:mt-[80px]'>
                  <CustomTrustpilot stars={true} large={true} vertical={false} changePosition={true}></CustomTrustpilot>
                </div>
              </div>
            </Container>
            <div id="testimonials-carousel" className='md:ml-[-16px] md:mr-[-16px]'>
              <SimpleCarousel
                loop={true}
                width={carouselWidth()}
                autoplay={false}
                animationSpeed={10000}
                doubleGap={16}
                controlsVisible={true}
                endLess={true}
                bullets={true}
                bulletsClass={'bullets--dark'}
                overflow={'hidden'}
                containerClass="checkout-testimonials-carousel2"
                nextHtml={<span className='carousel_3d_next_control--sec carousel_quiz_next_control'></span>}
                prevHtml={<span className='carousel_3d_prev_control--sec carousel_quiz_prev_control'></span>}
              >
                {testimonials.map((t, idx) => (
                  <SimpleCarouselItem key={t.name + idx}>
                    <TestimonialBig
                      name={t.name}
                      className="testimonial-hero-anim-less drop-shadow-xl"
                      text={t.text}
                      avatarUrl={t.avatarUrl}
                      caption={t.caption}
                      height={sm ? 270 : 255}
                    />
                  </SimpleCarouselItem>
                ))}
              </SimpleCarousel>
            </div>
          </section>
          <section className='bg-white py-[80px] xl:py-[140px]'>
            <Container>
              <div className='mb-[60px]'>
                <H2>FAQ</H2>
              </div>
              <Accordion accordion_items={eliteCoachingFAQs}/>
            </Container>
          </section>
        </RoundedContainer>
      </div>
    </>
  )
}

// @ts-ignore
EliteCoaching.getLayout = function getLayout(page: ReactNode) {
  return (
    <FatFooterLayout>
      {page}
    </FatFooterLayout>
  )
}

export default EliteCoaching
